import { searchStems } from "@productlane/lib/src/search-stems"

const MAX_STEMS = 10
const THREAD_TEXT_CHARS = 600
const DESCRIPTION_CHARS = 500
// One stem in the title, or two in the description; a lone description hit is noise.
const MIN_SCORE = 2

/** Word stems of what the thread is about: its title first, then the opening text. */
export function threadStems(thread: {
	title?: string | null
	text?: string | null
}): Array<string> {
	const text = (thread.text ?? "")
		.replace(/<[^>]*>/g, " ")
		.slice(0, THREAD_TEXT_CHARS)
	const stems = new Set([
		...searchStems([thread.title ?? ""]),
		...searchStems([text]),
	])
	return [...stems].slice(0, MAX_STEMS)
}

export function relatednessScore(
	stems: ReadonlyArray<string>,
	title: string,
	description: string | null | undefined,
): number {
	if (stems.length === 0) return 0
	const lowerTitle = title.toLowerCase()
	const lowerDescription = (description ?? "")
		.slice(0, DESCRIPTION_CHARS)
		.toLowerCase()
	let score = 0
	for (const stem of stems) {
		if (lowerTitle.includes(stem)) score += 2
		if (lowerDescription.includes(stem)) score += 1
	}
	return score >= MIN_SCORE ? score : 0
}

/**
 * Items that share words with the thread first, most shared first. Everything
 * else keeps its incoming order after them.
 */
export function rankByRelatedness<T>(
	items: Array<T>,
	stems: ReadonlyArray<string>,
	textOf: (item: T) => { title: string; description?: string | null },
): Array<T> {
	if (stems.length === 0) return items
	const scored = items.map((item, order) => {
		const { title, description } = textOf(item)
		return { item, order, score: relatednessScore(stems, title, description) }
	})
	if (!scored.some((entry) => entry.score > 0)) return items
	return scored
		.sort((a, b) => b.score - a.score || a.order - b.order)
		.map((entry) => entry.item)
}
